import { useEffect } from 'react';
import Favicon from 'react-favicon';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TranslationContextProvider } from '@omnipkg/ui-kit-web';
import { ThemeProvider } from 'styled-components';

import { projectIs } from './constants';
import NotificationsContext from './context/NotificationsContext';
import PageHeaderContext from './context/PageHeaderContext/PageHeaderContext';
import { useComposeTheme } from './hooks/useComposeTheme';
import useDeviceStatus from './hooks/useDeviceStatus';
import { useRoutes } from './hooks/useRoutes';
import { selectDeviceInfo } from './store/slices/initialSlice';
import { selectThemeSettings } from './store/slices/themeSlice';
import { GlobalStyle } from './theme/global';
import { ThemeMode } from './types/theme';

export default function App(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const routes = useRoutes();

  const { fontSize, isHighlightLinks, themeMode } =
    useSelector(selectThemeSettings);
  const deviceInfo = useSelector(selectDeviceInfo);

  const countryLanguage = deviceInfo?.country_settings?.languages || ['en'];

  const isBlackWhiteImgs = themeMode === ThemeMode.dark;

  const favicon =
    projectIs.omkaz || projectIs.omar || projectIs.mncdk
      ? 'faviconCommon.ico'
      : 'faviconOmis.ico';

  const theme = useComposeTheme();

  const queryUid = searchParams.get('uid');

  useDeviceStatus();

  useEffect(() => {
    if (queryUid) {
      navigate(`/${queryUid}`);
    }
    // We must check this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle
        fontSize={fontSize}
        isHighlightLinks={isHighlightLinks}
        isBlackWhiteImgs={isBlackWhiteImgs}
      />
      <Favicon url={favicon} />
      <TranslationContextProvider
        projectLangs={countryLanguage}
        defaultLang="en"
      >
        <NotificationsContext>
          <PageHeaderContext>{!queryUid && routes}</PageHeaderContext>
        </NotificationsContext>
      </TranslationContextProvider>
    </ThemeProvider>
  );
}
